import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {useAuth} from '../../services/use-auth';
import usePageMeta from '../../services/page-meta';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';
import './dashboard.scss';

function normalizeUrl(value) {
    if (!value) {
        return '';
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

function buildCardBackground(imageUrl) {
    const normalizedImage = normalizeUrl(imageUrl);

    if (!normalizedImage) {
        return 'linear-gradient(180deg, rgba(17, 24, 39, 0.14), rgba(17, 24, 39, 0.62))';
    }

    return `linear-gradient(180deg, rgba(17, 24, 39, 0.14), rgba(17, 24, 39, 0.62)), url('${normalizedImage}')`;
}

function formatNumericValue(value) {
    const numeric = Number(value);

    if (!Number.isFinite(numeric) || numeric <= 0) {
        return null;
    }

    if (Number.isInteger(numeric)) {
        return String(numeric);
    }

    return String(numeric).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
}

function formatCostLabel(cost, t) {
    const items = Array.isArray(cost) ? cost : [];

    if (items.length === 0) {
        return t('main.dashboard.labels.free', 'Free');
    }

    return items
        .map((entry) => {
            const value = formatNumericValue(entry?.value);
            const currencyCode = String(entry?.currencyCode || '').trim().toLowerCase();

            if (!value || !currencyCode) {
                return null;
            }

            return `${value} ${t(`app.currencies.${currencyCode}`, currencyCode.toUpperCase())}`;
        })
        .filter(Boolean)
        .join(' / ');
}

function formatRewardLabel(score, t) {
    const value = formatNumericValue(score);

    if (!value) {
        return null;
    }

    return `${value} ${t('app.currencies.cme', 'BPR points')}`;
}

function getTypeLabel(type, t) {
    if (type === 'testing') {
        return t('main.dashboard.labels.testing', 'Testing');
    }

    return t('main.dashboard.labels.course', 'Course');
}

function getStatBadge(code) {
    switch (code) {
        case 'duration':
            return '◷';
        case 'certificate':
            return '🎓';
        case 'reward':
            return '●';
        default:
            return '●';
    }
}

function buildCardStat(code, label, key) {
    if (!label) {
        return null;
    }

    return (
        <span className="stat" key={key}>
            <span className={`stat__ic stat__ic--${code}`} aria-hidden="true">
                {getStatBadge(code)}
            </span>
            <span className="stat__val">{label}</span>
        </span>
    );
}

function getUpdateLink(itemType, id) {
    switch (itemType) {
        case 'material':
            return `/material/${id}`;
        case 'survey':
            return `/surveys/${id}`;
        case 'conference':
            return `/conference/${id}`;
        case 'course':
            return `/course/${id}`;
        case 'testing':
            return `/testings/${id}`;
        default:
            return '/main';
    }
}

function getUpdateImage(item) {
    const image = String(item?.imageUrl || item?.image || '').replace(/^\/+/, '');
    if (!image) {
        return '';
    }

    if (item?.type === 'material' && !image.includes('img/content')) {
        return `/img/content/materials/images/cover/${image}`;
    }

    return `/${image}`;
}

function normalizeUpdateFeedItem(item) {
    if (!item || typeof item !== 'object') {
        return null;
    }

    const type = String(item.type || '').trim();
    const id = Number(item.id) || 0;

    return {
        ...item,
        id,
        type,
        title: String(item.title || ''),
        imageUrl: item.url ? item.imageUrl : getUpdateImage(item),
        dateLabel: String(item.dateLabel || item.dateAdded || ''),
        url: String(item.url || getUpdateLink(type, id)),
    };
}

function getPathnameFromUrl(url) {
    if (!url || typeof url !== 'string') {
        return null;
    }

    if (typeof window === 'undefined' || !window.location?.origin) {
        if (!url.startsWith('/')) {
            return null;
        }

        return url.split(/[?#]/, 1)[0] || '/';
    }

    try {
        const parsed = new URL(url, window.location.origin);

        if (parsed.origin !== window.location.origin) {
            return null;
        }

        return parsed.pathname;
    } catch (e) {
        return null;
    }
}

function isSpaRouteUrl(url) {
    const pathname = getPathnameFromUrl(url);
    if (!pathname) {
        return false;
    }

    if (
        /^\/conference\/\d+\/survey$/.test(pathname)
        || /^\/certifications(?:\/|$)/.test(pathname)
        || /^\/course\/\d+(?:\/|$)/.test(pathname)
        || /^\/testings\/\d+(?:\/|$)/.test(pathname)
    ) {
        return false;
    }

    return (
        /^\/$/.test(pathname)
        || /^\/(en|uk)$/.test(pathname)
        || /^\/about$/.test(pathname)
        || /^\/(en|uk)\/about$/.test(pathname)
        || /^\/instructions?$/.test(pathname)
        || /^\/(en|uk)\/instructions?$/.test(pathname)
        || /^\/404$/.test(pathname)
        || /^\/(en|uk)\/404$/.test(pathname)
        || /^\/500$/.test(pathname)
        || /^\/(en|uk)\/500$/.test(pathname)
        || /^\/main$/.test(pathname)
        || /^\/(en|uk)\/main$/.test(pathname)
        || /^\/account\/login$/.test(pathname)
        || /^\/account\/registration(?:\/pharm)?$/.test(pathname)
        || /^\/surveys(?:\/finished)?$/.test(pathname)
        || /^\/organizations$/.test(pathname)
        || /^\/(en|uk)\/organizations$/.test(pathname)
        || /^\/organization\/[^/]+(?:\/[^/]+)?$/.test(pathname)
        || /^\/(en|uk)\/organization\/[^/]+(?:\/[^/]+)?$/.test(pathname)
        || /^\/library(?:\/[^/]+(?:\/[^/]+)?)?$/.test(pathname)
        || /^\/material\/\d+(?:\/\d+)?$/.test(pathname)
        || /^\/promoCodes$/.test(pathname)
        || /^\/activatePromoCode$/.test(pathname)
        || /^\/profile(?:\/edit|\/complete|\/referrals)?$/.test(pathname)
        || /^\/(en|uk)\/profile\/referrals$/.test(pathname)
        || /^\/conferences(?:\/[^/]+)?$/.test(pathname)
        || /^\/conference\/\d+(?:\/(program|speakers(?:\/\d+)?|reports(?:\/\d+)?|myProgram|registration))?$/.test(pathname)
        || /^\/main\/certificates(?:\/|$)/.test(pathname)
        || /^\/portfolio(?:\/|$)/.test(pathname)
    );
}

function DashboardLink({url, className, children, ...rest}) {
    if (!url) {
        return null;
    }

    if (isSpaRouteUrl(url)) {
        return (
            <Link to={url} className={className} {...rest}>
                {children}
            </Link>
        );
    }

    return (
        <a href={url} className={className} {...rest}>
            {children}
        </a>
    );
}

export function MainDashboardSkeleton() {
    return (
        <section className="main-dashboard">
            <div className="container">
                <div className="main-dashboard__shell">
                    <div className="main-dashboard__skeleton main-dashboard__skeleton--hero" />
                    <div className="main-dashboard__skeleton main-dashboard__skeleton--updates" />
                    <div className="main-dashboard__skeleton main-dashboard__skeleton--section" />
                    <div className="main-dashboard__skeleton main-dashboard__skeleton--section" />
                </div>
            </div>
        </section>
    );
}

function MainCarouselSection({
    title,
    subtitle,
    viewAllUrl,
    viewAllLabel,
    previousLabel,
    nextLabel,
    items,
    renderItem,
}) {
    const trackRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    useEffect(() => {
        const track = trackRef.current;

        if (!track || items.length === 0) {
            setActiveIndex(0);
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return undefined;
        }

        const updateState = () => {
            const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
            const firstCard = track.querySelector('.main-dashboard__card');
            const step = firstCard ? firstCard.getBoundingClientRect().width + 16 : 336;
            const nextIndex = Math.max(0, Math.min(items.length - 1, Math.round(track.scrollLeft / Math.max(step, 1))));

            setActiveIndex(nextIndex);
            setCanScrollPrev(track.scrollLeft > 8);
            setCanScrollNext(track.scrollLeft < maxScroll - 8);
        };

        updateState();

        const handleScroll = () => {
            window.requestAnimationFrame(updateState);
        };

        track.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', updateState);

        return () => {
            track.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', updateState);
        };
    }, [items]);

    if (!items.length) {
        return null;
    }

    const scrollByStep = (direction) => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        const firstCard = track.querySelector('.main-dashboard__card');
        const step = firstCard ? firstCard.getBoundingClientRect().width + 16 : 336;

        track.scrollBy({
            left: direction * step,
            behavior: 'smooth',
        });
    };

    return (
        <div className="main-dashboard__section">
            <div className="main-dashboard__section-head">
                <div>
                    <h2>{title}</h2>
                    <p>{subtitle}</p>
                </div>
                <div className="main-dashboard__section-actions">
                    {items.length > 1 && (
                        <>
                            <div className="main-dashboard__dots" aria-hidden="true">
                                {items.map((item, index) => (
                                    <span
                                        className={`main-dashboard__dot ${index === activeIndex ? 'is-active' : ''}`}
                                        key={`dot-${title}-${item.id || index}`}
                                    />
                                ))}
                            </div>
                            <button
                                type="button"
                                className="main-dashboard__nav"
                                onClick={() => scrollByStep(-1)}
                                disabled={!canScrollPrev}
                                aria-label={previousLabel || 'Previous'}
                            >
                                {'<'}
                            </button>
                            <button
                                type="button"
                                className="main-dashboard__nav"
                                onClick={() => scrollByStep(1)}
                                disabled={!canScrollNext}
                                aria-label={nextLabel || 'Next'}
                            >
                                {'>'}
                            </button>
                        </>
                    )}
                    {viewAllUrl && viewAllLabel && (
                        <DashboardLink url={viewAllUrl} className="main-dashboard__view-all">
                            {viewAllLabel}
                        </DashboardLink>
                    )}
                </div>
            </div>

            <div className={`main-dashboard__carousel-shell ${canScrollPrev ? 'has-left-fade' : ''} ${canScrollNext ? 'has-right-fade' : ''}`}>
                <div className="main-dashboard__carousel" ref={trackRef}>
                    {items.map((item, index) => renderItem(item, index))}
                </div>
            </div>
        </div>
    );
}

function ContinueLearningCard({item, t}) {
    const rewardLabel = formatRewardLabel(item.score, t);
    const durationLabel = item.duration > 0 ? `${item.duration} ${t('main.dashboard.labels.minutes', 'min')}` : null;
    const progressPercent = Math.max(0, Math.min(100, Number(item.progressPercent) || 0));
    const stats = [
        buildCardStat('duration', durationLabel, `${item.type}-${item.id}-duration`),
        item.hasCertificate
            ? buildCardStat('certificate', t('main.dashboard.labels.certificate', 'Certificate'), `${item.type}-${item.id}-certificate`)
            : null,
        buildCardStat('reward', rewardLabel, `${item.type}-${item.id}-reward`),
    ].filter(Boolean);

    return (
        <article className="main-dashboard__card course-card course-card--dashboard course-card--progress" key={`continue-${item.type}-${item.id}`}>
            <DashboardLink
                url={item.primaryUrl}
                className="course-card__media course-card__media--with-progress"
                style={{backgroundImage: buildCardBackground(item.imageUrl)}}
            >
                <div className="main-dashboard__card-chip-row">
                    <span className="main-dashboard__pill">{getTypeLabel(item.type, t)}</span>
                </div>
                <div className="course-card__overlay">
                    <div className="course-card__kicker">{getTypeLabel(item.type, t)}</div>
                    <div className="course-card__title">{item.title}</div>
                </div>
                <div className="course-card__progress" aria-label={`${t('main.dashboard.labels.progress', 'Progress')} ${Math.round(progressPercent)}%`}>
                    <div className="course-card__progressbar" style={{width: `${progressPercent}%`}} />
                </div>
            </DashboardLink>

            <div className="course-card__body">
                <p className="course-card__desc">{item.shortDescription || item.title}</p>
                <div className="course-card__footer">
                    <div className="course-card__stats">{stats}</div>
                </div>
                <div className="course-card__actions">
                    <DashboardLink url={item.primaryUrl} className="dashboard-btn dashboard-btn--primary">
                        {t('main.dashboard.actions.continue', 'Continue')}
                    </DashboardLink>
                    {item.secondaryUrl && (
                        <DashboardLink url={item.secondaryUrl} className="dashboard-btn">
                            {t('main.dashboard.actions.details', 'Details')}
                        </DashboardLink>
                    )}
                </div>
            </div>
        </article>
    );
}

export function CertificateCard({item, t}) {
    const rewardLabel = formatRewardLabel(item.score, t);
    const stats = [
        buildCardStat('duration', item.completedAt, `certificate-${item.id}-completed`),
        buildCardStat('reward', rewardLabel, `certificate-${item.id}-reward`),
    ].filter(Boolean);

    return (
        <article className="main-dashboard__card course-card course-card--dashboard course-card--certificate" key={`certificate-${item.id}`}>
            <DashboardLink
                url={item.primaryUrl}
                className="course-card__media"
                style={{backgroundImage: buildCardBackground(item.imageUrl)}}
            >
                <div className="course-card__stamp">{t('main.dashboard.labels.completed', 'Completed')}</div>
                <div className="course-card__overlay">
                    <div className="course-card__kicker">{getTypeLabel(item.type, t)}</div>
                    <div className="course-card__title">{item.title}</div>
                </div>
            </DashboardLink>

            <div className="course-card__body">
                <p className="course-card__desc">{item.shortDescription || item.title}</p>
                <p className="course-card__period">{item.completedAt}</p>
                <div className="course-card__footer">
                    <div className="course-card__stats">{stats}</div>
                </div>
                <div className="course-card__actions">
                    <DashboardLink url={item.primaryUrl} className="dashboard-btn dashboard-btn--primary">
                        {t('main.dashboard.actions.certificate', 'Certificate')}
                    </DashboardLink>
                    {item.secondaryUrl && (
                        <DashboardLink url={item.secondaryUrl} className="dashboard-btn">
                            {t('main.dashboard.actions.details', 'Details')}
                        </DashboardLink>
                    )}
                </div>
            </div>
        </article>
    );
}

function RecommendationCard({item, t}) {
    const rewardLabel = formatRewardLabel(item.score, t);
    const durationLabel = item.duration > 0 ? `${item.duration} ${t('main.dashboard.labels.minutes', 'min')}` : null;
    const reasonLabel = t(`main.dashboard.reasons.${item.reasonCode}`, t('main.dashboard.reasons.peers', 'Popular among your peers'));
    const priceLabel = formatCostLabel(item.cost, t);
    const isFree = !Array.isArray(item.cost) || item.cost.length === 0;
    const stats = [
        buildCardStat('duration', durationLabel, `recommendation-${item.type}-${item.id}-duration`),
        item.hasCertificate
            ? buildCardStat('certificate', t('main.dashboard.labels.certificate', 'Certificate'), `recommendation-${item.type}-${item.id}-certificate`)
            : null,
        buildCardStat('reward', rewardLabel, `recommendation-${item.type}-${item.id}-reward`),
    ].filter(Boolean);

    return (
        <article className="main-dashboard__card course-card course-card--dashboard course-card--recommendation" key={`recommendation-${item.type}-${item.id}`}>
            <DashboardLink
                url={item.primaryUrl}
                className="course-card__media"
                style={{backgroundImage: buildCardBackground(item.imageUrl)}}
            >
                <span className={`badge course-card__price-chip ${isFree ? 'badge--free-price' : 'badge--price'}`}>
                    {priceLabel}
                </span>
                <div className="course-card__overlay">
                    <div className="course-card__kicker">{getTypeLabel(item.type, t)}</div>
                    <div className="course-card__title">{item.title}</div>
                </div>
            </DashboardLink>

            <div className="course-card__body">
                <p className="course-card__reason">{reasonLabel}</p>
                <p className="course-card__desc">{item.shortDescription || item.title}</p>
                <div className="course-card__footer">
                    <div className="course-card__stats">{stats}</div>
                </div>
                <div className="course-card__actions">
                    <DashboardLink url={item.primaryUrl} className="dashboard-btn dashboard-btn--primary">
                        {t('main.dashboard.actions.open', 'Open')}
                    </DashboardLink>
                    {item.secondaryUrl && (
                        <DashboardLink url={item.secondaryUrl} className="dashboard-btn">
                            {t('main.dashboard.actions.details', 'Details')}
                        </DashboardLink>
                    )}
                </div>
            </div>
        </article>
    );
}

function UpdateCard({item, t}) {
    const typeLabel = t(`main.dashboard.labels.${item.type}`, item.type);

    return (
        <DashboardLink url={item.url} className="main-dashboard__update-card">
            <div className="main-dashboard__update-media" style={{backgroundImage: buildCardBackground(item.imageUrl)}} />
            <div className="main-dashboard__update-body">
                <div className="main-dashboard__update-row">
                    <span className="main-dashboard__badge">{typeLabel}</span>
                    <span>{item.dateLabel}</span>
                </div>
                <div className="main-dashboard__update-title">{item.title}</div>
            </div>
        </DashboardLink>
    );
}

export default function Main() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('meta.main.title', t('main.title')),
        description: t('meta.main.description', t('app.description')),
        keywords: t('meta.main.keywords', t('app.keywords')),
    });

    const auth = useAuth();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [updatesItems, setUpdatesItems] = useState([]);
    const [nextUpdatesFetchPage, setNextUpdatesFetchPage] = useState(1);
    const [isUpdatesLoading, setIsUpdatesLoading] = useState(false);
    const [knownLastUpdatesPage, setKnownLastUpdatesPage] = useState(null);
    const [canScrollUpdatesPrev, setCanScrollUpdatesPrev] = useState(false);
    const [canScrollUpdatesNext, setCanScrollUpdatesNext] = useState(false);
    const updatesTrackRef = useRef(null);
    const updatesLoadingPageRef = useRef(null);
    const pendingUpdatesAdvanceRef = useRef(false);

    useEffect(() => {
        if (!auth?.user) {
            setData(null);
            setUpdatesItems([]);
            setNextUpdatesFetchPage(1);
            setKnownLastUpdatesPage(null);
            setIsUpdatesLoading(false);
            setCanScrollUpdatesPrev(false);
            setCanScrollUpdatesNext(false);
            setNotFound(false);
            setIsLoading(false);
            return;
        }

        let active = true;
        setIsLoading(true);

        fetch('/api/v1/main', {credentials: 'same-origin'})
            .then(async (response) => {
                const result = await response.json().catch(() => null);
                const {kind, redirectUrl} = parseRouteResponse(response, result);

                if (kind === 'not_found') {
                    if (active) {
                        setNotFound(true);
                    }

                    return null;
                }

                if ((kind === 'unauthorized' || kind === 'redirect' || kind === 'error') && redirectUrl) {
                    window.location.href = redirectUrl;
                    return null;
                }

                if (kind === 'unauthorized') {
                    return null;
                }

                if (kind === 'error') {
                    throw new Error('Cannot load main page');
                }

                if (active) {
                    setNotFound(false);
                }

                return result;
            })
            .then((result) => {
                if (!active) {
                    return;
                }

                const initialUpdates = (Array.isArray(result?.updates?.items) ? result.updates.items : [])
                    .map(normalizeUpdateFeedItem)
                    .filter(Boolean);

                setData(result);
                setUpdatesItems(initialUpdates);
                setNextUpdatesFetchPage(initialUpdates.length ? 2 : 1);
                setIsUpdatesLoading(false);
                setKnownLastUpdatesPage(initialUpdates.length > 0 && initialUpdates.length < 3 ? 1 : null);
                updatesLoadingPageRef.current = null;
                pendingUpdatesAdvanceRef.current = false;
            })
            .catch(() => {
                if (!active) {
                    return;
                }

                setData(null);
            })
            .finally(() => {
                if (!active) {
                    return;
                }

                setIsLoading(false);
            });

        return () => {
            active = false;
        };
    }, [auth?.user]);

    const loadUpdatesPage = (page) => {
        if (page <= 0 || null !== updatesLoadingPageRef.current) {
            return;
        }

        updatesLoadingPageRef.current = page;
        setIsUpdatesLoading(true);

        fetch(`/api/v1/quest/updatesFeed/${page}`, {credentials: 'same-origin'})
            .then((response) => {
                if (response.status === 404) {
                    setKnownLastUpdatesPage(Math.max(0, page - 1));
                    return null;
                }

                if (!response.ok) {
                    throw new Error('Cannot load updates');
                }

                return response.json();
            })
            .then((result) => {
                if (!result) {
                    return;
                }

                const normalizedItems = (Array.isArray(result) ? result : [])
                    .map(normalizeUpdateFeedItem)
                    .filter(Boolean);

                if (page > 1 && normalizedItems.length === 0) {
                    setKnownLastUpdatesPage(Math.max(1, page - 1));
                    return;
                }

                setUpdatesItems((prev) => [...prev, ...normalizedItems]);
                setNextUpdatesFetchPage(page + 1);

                if (normalizedItems.length < 3) {
                    setKnownLastUpdatesPage(page);
                }
            })
            .catch(() => {
                // Keep the current update page visible when the next page cannot be loaded.
            })
            .finally(() => {
                updatesLoadingPageRef.current = null;
                setIsUpdatesLoading(false);
            });
    };

    useEffect(() => {
        if (updatesItems.length !== 3) {
            return;
        }

        if (nextUpdatesFetchPage !== 2) {
            return;
        }

        if (null !== knownLastUpdatesPage || null !== updatesLoadingPageRef.current) {
            return;
        }

        loadUpdatesPage(2);
    }, [updatesItems.length, nextUpdatesFetchPage, knownLastUpdatesPage]);

    const goToNextUpdates = () => {
        if (isUpdatesLoading) {
            return;
        }

        const track = updatesTrackRef.current;
        if (!track) {
            return;
        }

        const firstCard = track.querySelector('.main-dashboard__update-card');
        const step = firstCard ? firstCard.getBoundingClientRect().width + 16 : track.clientWidth;
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

        if (track.scrollLeft < maxScroll - 8) {
            track.scrollBy({
                left: step,
                behavior: 'smooth',
            });
            return;
        }

        if (null !== knownLastUpdatesPage && nextUpdatesFetchPage > knownLastUpdatesPage) {
            return;
        }

        pendingUpdatesAdvanceRef.current = true;
        loadUpdatesPage(nextUpdatesFetchPage);
    };

    const prevUpdatesPage = () => {
        const track = updatesTrackRef.current;
        if (!track) {
            return;
        }

        const firstCard = track.querySelector('.main-dashboard__update-card');
        const step = firstCard ? firstCard.getBoundingClientRect().width + 16 : track.clientWidth;

        track.scrollBy({
            left: -step,
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        const track = updatesTrackRef.current;
        if (!track) {
            return;
        }

        if (!pendingUpdatesAdvanceRef.current) {
            return;
        }

        pendingUpdatesAdvanceRef.current = false;

        const firstCard = track.querySelector('.main-dashboard__update-card');
        const step = firstCard ? firstCard.getBoundingClientRect().width + 16 : track.clientWidth;

        window.requestAnimationFrame(() => {
            track.scrollBy({
                left: step,
                behavior: 'smooth',
            });
        });
    }, [updatesItems]);

    useEffect(() => {
        const track = updatesTrackRef.current;

        if (!track || updatesItems.length === 0) {
            setCanScrollUpdatesPrev(false);
            setCanScrollUpdatesNext(false);
            return undefined;
        }

        const updateState = () => {
            const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
            const hasMoreUpdates = null === knownLastUpdatesPage || nextUpdatesFetchPage <= knownLastUpdatesPage;

            setCanScrollUpdatesPrev(track.scrollLeft > 8);
            setCanScrollUpdatesNext(track.scrollLeft < maxScroll - 8 || hasMoreUpdates);
        };

        updateState();

        const handleScroll = () => {
            window.requestAnimationFrame(updateState);
        };

        track.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', updateState);

        return () => {
            track.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', updateState);
        };
    }, [updatesItems, nextUpdatesFetchPage, knownLastUpdatesPage]);

    if (!auth?.user) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (isLoading && !data) {
        return <MainDashboardSkeleton />;
    }

    if (!data) {
        return (
            <section className="main-dashboard">
                <div className="container">
                    <div className="main-dashboard__shell">
                        <div className="main-dashboard__status">
                            {t('main.dashboard.unavailable', 'Dashboard is temporarily unavailable.')}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    const profileHeader = data.profileHeader || {};
    const continueLearning = data.continueLearning || {items: []};
    const recommendations = data.recommendations || {items: []};
    const greetingName = profileHeader.greetingName || profileHeader.displayName || t('menu.defaultUsername');
    const displayName = profileHeader.displayName || t('menu.defaultUsername');
    const avatarLetter = String(greetingName || '').trim().charAt(0).toUpperCase() || 'A';
    const profileSubtitle = profileHeader.subtitle || t('main.dashboard.profileHint', 'Complete your profile to improve recommendations.');
    const balances = profileHeader.balances || {};
    const visibleUpdates = updatesItems;
    const hasMoreUpdates = visibleUpdates.length > 0
        && (null === knownLastUpdatesPage || nextUpdatesFetchPage <= knownLastUpdatesPage);

    const handleUpdatesCarouselScroll = () => {
        if (typeof window === 'undefined' || window.innerWidth > 767) {
            return;
        }

        if (!hasMoreUpdates || null !== updatesLoadingPageRef.current) {
            return;
        }

        const track = updatesTrackRef.current;
        if (!track) {
            return;
        }

        const maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll <= 0) {
            return;
        }

        if (track.scrollLeft >= maxScroll - 24) {
            loadUpdatesPage(nextUpdatesFetchPage);
        }
    };

    return (
        <section className="main-dashboard">
            <div className="container">
                <div className="main-dashboard__shell">
                    <header className="main-dashboard__hero">
                        <div className="main-dashboard__identity">
                            <div className={`main-dashboard__avatar ${profileHeader.hasAvatar ? 'has-image' : ''}`}>
                                {profileHeader.hasAvatar && profileHeader.avatarUrl ? (
                                    <img src={profileHeader.avatarUrl} alt="" />
                                ) : (
                                    <span>{avatarLetter}</span>
                                )}
                            </div>
                            <div className="main-dashboard__identity-copy">
                                <h1>{t('main.dashboard.hello', 'Hello')}, {greetingName}</h1>
                                <div className="main-dashboard__identity-name">{displayName}</div>
                                <p>{profileSubtitle}</p>
                            </div>
                        </div>

                        <div className="main-dashboard__hero-side">
                            <div className="main-dashboard__balances" aria-label={t('main.dashboard.labels.balances', 'Balances')}>
                                <div className="main-dashboard__balance">
                                    <span>{t('app.currencies.cme', 'CME')}</span>
                                    <strong>{Number(balances.cme) || 0}</strong>
                                </div>
                                <div className="main-dashboard__balance">
                                    <span>{t('app.currencies.soc', 'Soc')}</span>
                                    <strong>{Number(balances.soc) || 0}</strong>
                                </div>
                                <div className="main-dashboard__balance">
                                    <span>{t('app.currencies.edu', 'Edu')}</span>
                                    <strong>{Number(balances.edu) || 0}</strong>
                                </div>
                                <div className="main-dashboard__balance">
                                    <span>{t('app.currencies.bon', 'Bonus')}</span>
                                    <strong>{Number(balances.bon) || 0}</strong>
                                </div>
                            </div>

                            <div className="main-dashboard__hero-actions">
                                <DashboardLink url={profileHeader.primaryCta?.url || '/profile'} className="dashboard-btn dashboard-btn--primary">
                                    {t('main.summary', 'Summary')}
                                </DashboardLink>
                                {profileHeader.secondaryCta?.url && (
                                    <DashboardLink url={profileHeader.secondaryCta.url} className="dashboard-btn dashboard-btn--ghost">
                                        {t('main.dashboard.certificates.title', 'Certificates')}
                                    </DashboardLink>
                                )}
                            </div>
                        </div>
                    </header>

                    <AdvertiseBlock
                        unitId="title1"
                        blockLocation="dashboard-top"
                        className="main-dashboard__ad"
                    />

                    {!!visibleUpdates.length && (
                        <div className="main-dashboard__section" id="main-updates">
                            <div className="main-dashboard__section-head">
                                <div>
                                    <h2>{t('main.dashboard.updates.title', 'Updates')}</h2>
                                    <p>{t('main.dashboard.updates.subtitle', "What's new: courses, events, materials.")}</p>
                                </div>
                                <div className="main-dashboard__section-actions">
                                    {(canScrollUpdatesPrev || canScrollUpdatesNext) && (
                                        <>
                                            <button
                                                type="button"
                                                className="main-dashboard__nav"
                                                onClick={prevUpdatesPage}
                                                disabled={!canScrollUpdatesPrev}
                                                aria-label={t('main.dashboard.actions.previous', 'Previous')}
                                            >
                                                {'<'}
                                            </button>
                                            <button
                                                type="button"
                                                className="main-dashboard__nav"
                                                onClick={goToNextUpdates}
                                                disabled={!canScrollUpdatesNext || isUpdatesLoading}
                                                aria-label={t('main.dashboard.actions.next', 'Next')}
                                            >
                                                {'>'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="main-dashboard__carousel-shell">
                                <div
                                    className="main-dashboard__carousel main-dashboard__carousel--updates"
                                    ref={updatesTrackRef}
                                    onScroll={handleUpdatesCarouselScroll}
                                >
                                    {visibleUpdates.map((item) => (
                                        <UpdateCard item={item} t={t} key={`update-${item.type}-${item.id}`} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <MainCarouselSection
                        title={t('main.dashboard.continue.title', 'Continue learning')}
                        subtitle={t('main.dashboard.continue.subtitle', 'Only the items already in progress.')}
                        viewAllUrl={continueLearning.viewAllUrl}
                        viewAllLabel={continueLearning.viewAllUrl ? t('main.dashboard.continue.viewAll', 'All in progress') : null}
                        previousLabel={t('main.dashboard.actions.previous', 'Previous')}
                        nextLabel={t('main.dashboard.actions.next', 'Next')}
                        items={Array.isArray(continueLearning.items) ? continueLearning.items : []}
                        renderItem={(item) => <ContinueLearningCard item={item} t={t} key={`continue-${item.type}-${item.id}`} />}
                    />

                    <MainCarouselSection
                        title={t('main.dashboard.recommendations.title', 'Recommended for BPR')}
                        subtitle={t('main.dashboard.recommendations.subtitle', 'Selected by speciality and what peers choose next.')}
                        viewAllUrl={recommendations.viewAllUrl}
                        viewAllLabel={recommendations.viewAllUrl ? t('main.dashboard.actions.open', 'Open') : null}
                        previousLabel={t('main.dashboard.actions.previous', 'Previous')}
                        nextLabel={t('main.dashboard.actions.next', 'Next')}
                        items={Array.isArray(recommendations.items) ? recommendations.items : []}
                        renderItem={(item) => <RecommendationCard item={item} t={t} key={`recommendation-${item.type}-${item.id}`} />}
                    />

                    <AdvertiseBlock
                        unitId="title2"
                        blockLocation="dashboard-mid"
                        className="main-dashboard__ad"
                    />

                    <AdvertiseBlock
                        unitId="title3"
                        blockLocation="dashboard-bottom"
                        className="main-dashboard__ad"
                    />
                </div>
            </div>
        </section>
    );
}
